import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useModalPopover, usePopoverContainer } from './modalContext';
import { placePopover, POPOVER_MAX_HEIGHT } from './popoverPlace';
import { usePopoverEntered } from './usePopoverEntered';

export function useSelectPopover<E extends HTMLElement>(
  kind: 'select' | 'combobox',
  content: unknown,
  compact = false,
) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useModalPopover(open, close);
  const entered = usePopoverEntered(open);
  const container = usePopoverContainer();
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<E>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const anchorRect = useRef<DOMRect | null>(null);
  const [position, setPosition] = useState<{
    top: number; left: number; width?: number; maxHeight?: number;
  }>({ top: 0, left: 0 });
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;
    const rect = trigger.getBoundingClientRect();
    anchorRect.current = rect;
    if (!compact) popover.style.width = rect.width + 'px';
    const width = popover.getBoundingClientRect().width;
    const height = Math.min(popover.scrollHeight + 2, POPOVER_MAX_HEIGHT);
    const placed = placePopover(rect, width, height);
    setPosition({
      top: placed.top,
      left: placed.left,
      width: compact ? undefined : rect.width,
      maxHeight: Math.min(placed.maxHeight, POPOVER_MAX_HEIGHT),
    });
  }, [compact]);
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition, content]);
  useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapRef.current?.contains(target) && !popoverRef.current?.contains(target)) close();
    };
    const onScroll = (event: Event) => {
      if (popoverRef.current?.contains(event.target as Node)) return;
      if (kind === 'select') {
        const before = anchorRect.current;
        const current = triggerRef.current?.getBoundingClientRect();
        if (before && current && before.top === current.top && before.left === current.left) return;
      }
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (kind === 'combobox' && event.key === 'Escape') close();
    };
    const onResize = () => flushSync(reposition);
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onOutside, true);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, kind, close, reposition]);
  return {
    open, setOpen, close, entered, container, wrapRef, triggerRef, popoverRef,
    style: { position: 'fixed' as const, ...position },
  };
}
